import { useEffect, useRef, useState } from 'react';
import './App.css';

const EMAIL = 'support@ascentenvision.com';

const STATS = [
  { value: '6–10', label: 'weeks to a usable MVP' },
  { value: '2–4', label: 'senior engineers per project' },
  { value: '100%', label: 'code and infra you own' },
];

const SERVICES = [
  {
    number: '01',
    title: 'AI & ML integration',
    body: 'Retrieval, agents, and model-backed features wired into a real product — with evaluation and cost controls, not a demo that breaks in week two.',
  },
  {
    number: '02',
    title: 'AWS Cloud & DevSecOps',
    body: 'Deploys you can run yourself: CI, environments, observability, security baked into the pipeline, and an AWS bill that makes sense at your stage.',
  },
  {
    number: '03',
    title: 'Web app development',
    body: 'Dashboards, marketplaces, internal tools, customer portals. Typed end to end, tested where it counts, fast on a bad connection.',
  },
  {
    number: '04',
    title: 'Mobile app development',
    body: 'Native and cross-platform apps for iOS and Android — built to pass review, ship weekly, and survive the second year of feature requests.',
  },
  {
    number: '05',
    title: 'Technical consulting & architecture',
    body: 'Scope, sequence, and system design before a line of code. Useful when you need a plan you can hand to investors or to your own first hires.',
  },
];

const PROCESS = [
  {
    stage: 'Week 0',
    title: 'Shape it',
    body: 'A paid two-week discovery: what to build first, what to cut, and a fixed scope with a number attached.',
  },
  {
    stage: 'Weeks 1–8',
    title: 'Build in the open',
    body: 'Weekly builds in your hands, a shared board, and direct access to the engineers. No status theatre.',
  },
  {
    stage: 'Launch',
    title: 'Ship & measure',
    body: 'Store submission, monitoring, and the first round of fixes from real usage rather than guesses.',
  },
  {
    stage: 'After',
    title: 'Hand over',
    body: 'Documentation, walkthroughs, and a clean handoff to your team — or we stay on retainer. Your call.',
  },
];

const STACK = [
  { group: 'Mobile', items: ['Swift / SwiftUI', 'Kotlin / Compose', 'React Native', 'Expo'] },
  { group: 'Web', items: ['TypeScript', 'React / Next.js', 'Java / Spring Boot', 'Python / FastAPI'] },
  { group: 'Data & AI', items: ['Evaluation harnesses', 'Kafka', 'LLM APIs', 'Claude'] },
  { group: 'Infrastructure', items: ['AWS', 'AWS CDK', 'Terraform', 'Containerization'] },
];

function Wordmark() {
  return (
    <>
      <span className="dot" />
      <span className="wordmark">Ascent Envision</span>
    </>
  );
}

// Served from the site's own domain via CloudFront, so no origin needed.
const CONTACT_ENDPOINT = '/api/contact';

// Cloudflare's documented always-passes test key, used until a real one is set at build time.
// The site key is public by design — it ships in the bundle. The secret lives in SSM.
const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

// Mounts the Turnstile widget once the script has loaded, and hands back the token it issues.
function useTurnstile(onToken) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const mount = () => {
      if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => onToken(null),
      });
    };

    // The script is loaded async in index.html, so it may not be there on first render.
    let poll;
    if (window.turnstile) {
      mount();
    } else {
      poll = setInterval(() => {
        if (window.turnstile) {
          clearInterval(poll);
          mount();
        }
      }, 200);
    }

    return () => {
      cancelled = true;
      clearInterval(poll);
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onToken]);

  // A token is good for one submission; reset the widget after a failure so a retry can pass.
  const reset = () => {
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
    onToken(null);
  };

  return { containerRef, reset };
}

function ContactForm() {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const tokenRef = useRef(null);

  // A ref, not state: the token changing must not re-render the form mid-typing.
  const onToken = useRef((token) => {
    tokenRef.current = token;
  }).current;
  const { containerRef, reset: resetTurnstile } = useTurnstile(onToken);

  const onSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.target);
    setStatus('sending');
    setError(null);

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.get('name'),
          email: data.get('email'),
          stage: data.get('stage'),
          brief: data.get('brief'),
          company: data.get('company'),
          turnstileToken: tokenRef.current,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'That did not go through.');
      }

      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err.message);
      resetTurnstile();
    }
  };

  const sent = status === 'sent';

  return (
    <form className="contact-form" onSubmit={onSubmit}>
      <label className="field">
        <span className="field-label">Name</span>
        <input type="text" name="name" placeholder="Jane Okafor" required />
      </label>
      <label className="field">
        <span className="field-label">Email</span>
        <input type="email" name="email" placeholder="jane@company.com" required />
      </label>
      <label className="field">
        <span className="field-label">Where you are</span>
        <select name="stage" defaultValue="Idea on paper">
          <option>Idea on paper</option>
          <option>Designs ready, need building</option>
          <option>Live product, need help</option>
          <option>Just exploring</option>
        </select>
      </label>
      <label className="field">
        <span className="field-label">What you're building</span>
        <textarea
          name="brief"
          rows="4"
          placeholder="A booking app for independent physiotherapists…"
          required
        />
      </label>
      {/* Bots fill in every field they find; people never see this one. */}
      <input
        className="honeypot"
        type="text"
        name="company"
        tabIndex="-1"
        autoComplete="off"
        aria-hidden="true"
      />
      <div className="turnstile" ref={containerRef} />
      <button type="submit" disabled={status === 'sending' || sent}>
        {sent ? 'Sent' : status === 'sending' ? 'Sending…' : 'Send it over'}
      </button>
      {sent && (
        <div className="form-note">Thanks — we'll be in touch within two business days.</div>
      )}
      {status === 'error' && (
        <div className="form-note form-note-error" role="alert">
          {error} Please email us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
        </div>
      )}
    </form>
  );
}

function App() {
  return (
    <div className="page">
      <header className="site-header">
        <div className="shell header-inner">
          <a className="brand" href="#top">
            <Wordmark />
          </a>
          <nav className="nav">
            <a href="#services">Services</a>
            <a href="#stack">Stack</a>
            <a href="#how">How we work</a>
            <a className="btn btn-dark" href="#contact">
              Email us
            </a>
          </nav>
        </div>
      </header>

      <section id="top" className="shell hero">
        <div className="eyebrow">Software consulting &amp; app development</div>
        <h1>We build the first version founders bet on.</h1>
        <p className="hero-lede">
          A small senior team that takes an idea from sketch to shipped product — mobile, web, and
          the infrastructure underneath. No account managers, no handoffs. You work with the people
          writing the code.
        </p>
        <div className="hero-actions">
          <a className="btn btn-accent" href="#contact">
            Email us about your project
          </a>
          <a className="btn btn-outline" href="#services">
            See what we do
          </a>
        </div>
        <div className="stats">
          {STATS.map((stat) => (
            <div className="stat" key={stat.label}>
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="services" className="band band-tint">
        <div className="shell">
          <div className="section-head">
            <h2>What we do</h2>
            <p>Five practices, one team. Most engagements use two or three of them together.</p>
          </div>
          <div className="service-grid">
            {SERVICES.map((service) => (
              <div className="service" key={service.number}>
                <div className="service-number">{service.number}</div>
                <h3>{service.title}</h3>
                <p>{service.body}</p>
              </div>
            ))}
            <div className="service service-cta">
              <p>Not sure which of these you need? That's usually the first conversation.</p>
              <a href="#contact">Email us →</a>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="band">
        <div className="shell">
          <h2 className="section-title">How we work</h2>
          <div className="process-grid">
            {PROCESS.map((step, i) => (
              <div className={i === 0 ? 'step step-current' : 'step'} key={step.title}>
                <div className="step-stage">{step.stage}</div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="stack" className="band band-tint">
        <div className="shell">
          <div className="section-head section-head-tight">
            <h2>Tech stack</h2>
            <p>Boring, well-supported tools. We pick per project, not per fashion cycle.</p>
          </div>
          <div className="stack-grid">
            {STACK.map((column) => (
              <div key={column.group}>
                <div className="stack-heading">{column.group}</div>
                <div className="stack-items">
                  {column.items.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="band band-contact">
        <div className="shell contact-grid">
          <div>
            <h2 className="contact-title">Tell us what you're building.</h2>
            <p className="contact-lede">
              Send a few lines about the idea and where you are with it. You'll get a real reply from
              an engineer within two business days.
            </p>
            <a className="contact-email" href={`mailto:${EMAIL}`}>
              {EMAIL}
            </a>
            <div className="contact-meta">
              <div>Mon–Fri, 9–6 CET</div>
              <div>Remote-first · EU &amp; US hours</div>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      <footer className="site-footer">
        <div className="shell footer-inner">
          <div className="brand">
            <Wordmark />
          </div>
          <div className="footer-legal">
            © 2026 Ascent Envision LLC · Software consulting &amp; app development
          </div>
          <a className="footer-email" href={`mailto:${EMAIL}`}>
            {EMAIL}
          </a>
        </div>
      </footer>
    </div>
  );
}

export default App;
